"use client";

import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, type Variants } from "motion/react";
import { StylizedGlobe } from "@/components/StylizedGlobe";

/* ─── letter-by-letter reveal for the wordmark ─── */
const WORDS = ["Pale", "Blue"];
const QUOTE =
  "Every valley, coast, and peak in Pakistan is part of one small world, the \"Pale Blue Dot\", adrift in a great dark. We travel it gently, and leave it whole.";

/* The line the site is named after. Opens the hero above the wordmark, holds
   long enough to actually be read, then clears itself away for good. */
const SAGAN_QUOTE = "Look again at that dot. That's here. That's home. That's us.";

/* One pass, in seconds: 0.25s before it starts, then fade in / hold / fade out
   across 10s split by the `times` array below - roughly 0.9s in, 7.3s held,
   1.8s out. The fades are the same speed they were at the old 7.2s total; the
   extra time all went into the hold, so the motion looks identical and the
   line just stays readable for longer. */
const SAGAN_DELAY = 0.25;
const SAGAN_DURATION = 10;
const SAGAN_TIMES = [0, 0.094, 0.827, 1];

/* Phones only (below Tailwind's `sm`, 640px): the philosophy quote holds for
   8s, then fades out and the wordmark + "Pakistan Tourism Almanac" slide down
   into its place and stay (owner, 26 Sept 2026). The quote keeps its space in
   the layout, so the search box underneath never moves. */
const MOBILE_QUERY = "(max-width: 639px)";
const QUOTE_HOLD_MS = 8000;

const letterVariant: Variants = {
  hidden: { opacity: 0, y: 32, rotateX: -60, filter: "blur(8px)" },
  show: (i: number) => ({
    opacity: 1,
    y: 0,
    rotateX: 0,
    filter: "blur(0px)",
    transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1], delay: i * 0.045 },
  }),
};

const fadeUp: Variants = {
  hidden: { opacity: 0, y: 20, filter: "blur(4px)" },
  show: (d = 0) => ({
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: d },
  }),
};

const globeAnim: Variants = {
  hidden: { opacity: 0, scale: 0.7 },
  show: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1], delay: 0.4 },
  },
};

/* shimmer glow pulse on title */
const shimmerVariant: Variants = {
  idle: { textShadow: "0 0 0px transparent" },
  glow: {
    textShadow: [
      "0 0 0px transparent",
      "0 0 28px rgba(96,165,250,0.7), 0 0 60px rgba(96,165,250,0.35)",
      "0 0 0px transparent",
    ],
    transition: { duration: 2.2, ease: "easeInOut", delay: 1.0 },
  },
};


/*
 * Everything in the hero is visible in the server-rendered HTML.
 *
 * The wordmark letters, globe, "Pakistan Tourism Almanac" and the philosophy
 * quote used to start at opacity 0 and animate in one after another once the
 * script loaded, with the quote typed out after a 1.4s pause. On a real
 * connection that meant a blank hero for seconds, which read as a slow site.
 * `initial={false}` renders their finished state straight away. What still
 * moves never hides content: the title glow, the underline sweep, the spinning
 * globe, and the Carl Sagan epigraph that appears and clears above the wordmark.
 */
export function HeroBrand() {
  const reduceMotion = useReducedMotion();
  // Unmounted rather than left at opacity:0, so it doesn't linger in the
  // accessibility tree as invisible-but-readable text once it's played out.
  const [saganShown, setSaganShown] = useState(true);
  let letterIdx = 0;

  const titleRef = useRef<HTMLDivElement>(null);
  const quoteRef = useRef<HTMLElement>(null);
  const [quoteDone, setQuoteDone] = useState(false);
  // On phones the Carl Sagan line stays for good, above the wordmark
  // (owner, 26 Sept 2026); elsewhere it plays once and clears as before.
  const [isMobile, setIsMobile] = useState(false);
  // How far the title moves down to sit centred where the quote was.
  const [titleShift, setTitleShift] = useState(0);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    let timer: number | undefined;
    const start = () => {
      window.clearTimeout(timer);
      setIsMobile(media.matches);
      if (media.matches) {
        timer = window.setTimeout(() => setQuoteDone(true), QUOTE_HOLD_MS);
      } else {
        setQuoteDone(false);
      }
    };
    start();
    media.addEventListener("change", start);
    return () => {
      window.clearTimeout(timer);
      media.removeEventListener("change", start);
    };
  }, []);

  useEffect(() => {
    if (!quoteDone) return;
    const measure = () => {
      const title = titleRef.current;
      const quote = quoteRef.current;
      if (!title || !quote) return;
      const titleMid = title.offsetTop + title.offsetHeight / 2;
      const quoteMid = quote.offsetTop + quote.offsetHeight / 2;
      setTitleShift(Math.max(0, quoteMid - titleMid));
    };
    measure();
    const observer = new ResizeObserver(measure);
    if (quoteRef.current) observer.observe(quoteRef.current);
    return () => observer.disconnect();
  }, [quoteDone]);

  return (
    <div className="relative flex flex-col gap-0" style={{ perspective: "600px" }}>

      {/* Real page heading for SEO/screen readers; the animated letterform below
          is visual art and would read letter-by-letter, so it stays aria-hidden. */}
      <h1 className="sr-only">
        Green Tourism Pakistan: Pakistan travel guides, resorts and tours
      </h1>

      {/* The title block: slides down into the quote's place on phones once
          the quote has played (see MOBILE_QUERY). The Sagan epigraph sits
          inside it, anchored above the wordmark, so on phones it stays with
          the title (owner, 26 Sept 2026). */}
      <motion.div
        ref={titleRef}
        className="relative flex flex-col gap-0"
        initial={false}
        animate={{ y: quoteDone ? titleShift : 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.9, ease: [0.16, 1, 0.3, 1] }}
      >
          {/* ── Carl Sagan, above the wordmark: appears on load; on phones it stays, elsewhere it holds and vanishes ──
              Absolutely positioned off the top edge (bottom-full) on purpose: it
              has to be able to disappear without the wordmark and everything under
              it jumping up to fill the gap. Sitting outside the flex flow means its
              entrance and exit cost zero layout shift. Runs on every mount, so a
              reload or a client-side return to the homepage replays it. */}
          {saganShown && (
            <motion.figure
              // On mobile the box has to stop short of the GreenPakFundBadge, which
              // is pinned `absolute right-4 top-4` at z-20 over this same band and
              // would otherwise clip the end of the quote. The badge is 44px wide
              // and sits 16px off the viewport edge, i.e. 44px inside this
              // container's right edge - 3.5rem reserves that plus breathing room.
              // From sm up the badge moves to right-7/top-7 and the container is
              // far wider than 520px, so there's nothing to avoid.
              className="pointer-events-none absolute bottom-full left-0 mb-2 w-[calc(100%-3.5rem)] max-w-[520px] sm:mb-4 sm:w-full"
              initial={{
                opacity: 0,
                y: reduceMotion ? 0 : 10,
                filter: reduceMotion ? "blur(0px)" : "blur(6px)",
              }}
              animate={
                isMobile
                  ? { opacity: 1, y: 0, filter: "blur(0px)" }
                  : {
                      opacity: [0, 1, 1, 0],
                      y: reduceMotion ? 0 : [10, 0, 0, -8],
                      filter: reduceMotion
                        ? "blur(0px)"
                        : ["blur(6px)", "blur(0px)", "blur(0px)", "blur(6px)"],
                    }
              }
              transition={
                isMobile
                  ? { duration: reduceMotion ? 0 : 0.9, ease: "easeOut", delay: SAGAN_DELAY }
                  : { duration: SAGAN_DURATION, times: SAGAN_TIMES, ease: "easeInOut", delay: SAGAN_DELAY }
              }
              onAnimationComplete={() => {
                if (!isMobile) setSaganShown(false);
              }}
            >
              {/* Bare text, no card. It reads as an epigraph to the wordmark rather
                  than another panel competing with the philosophy quote below, and
                  dropping the padding/border keeps it clear of the header on narrow
                  phones - this box grows upward, so every extra line eats into that
                  gap. Legibility over the photo comes from the same text-shadow the
                  Almanac line below uses, not from a backdrop. */}
              <blockquote
                className="m-0 font-[family-name:var(--font-display)] text-[0.72rem] italic leading-snug text-[rgba(255,247,231,0.94)] sm:text-[clamp(0.85rem,1.1vw,1rem)]"
                style={{ textShadow: "0 2px 12px rgba(0,0,0,0.9), 0 1px 3px rgba(0,0,0,0.8)" }}
              >
                &ldquo;{SAGAN_QUOTE}&rdquo;{" "}
                <span className="whitespace-nowrap not-italic opacity-70">~ Carl Sagan</span>
              </blockquote>
            </motion.figure>
          )}

        {/* ── PALE BLUE DOT wordmark ── */}
        <motion.div
          aria-hidden
          className="flex flex-col gap-1"
          initial={false}
          animate="show"
        >
          {/* line 1: PALE BLUE */}
          <motion.div
            variants={shimmerVariant}
            initial="idle"
            animate="glow"
            className="inline-flex items-center gap-[0.26em] font-[family-name:var(--font-din)] font-black uppercase leading-none tracking-[0.04em] text-[clamp(1.6rem,9vw,4.2rem)] text-[#60a5fa] select-none"
            style={{ textShadow: "0 2px 16px rgba(0,0,0,0.9), 0 0 48px rgba(37,99,235,0.5)" }}
          >
            {WORDS.map((word) =>
              word.split("").map((ch) => {
                const idx = letterIdx++;
                return (
                  <motion.span
                    key={`${word}-${idx}`}
                    custom={idx}
                    variants={letterVariant}
                    initial={false}
                    animate="show"
                    className="inline-block"
                  >
                    {ch === " " ? " " : ch}
                  </motion.span>
                );
              })
            ).reduce<React.ReactNode[]>((acc, letters, wi) => {
              if (wi > 0) acc.push(<span key={`sp-${wi}`} className="inline-block w-[0.26em]" />);
              acc.push(...letters);
              return acc;
            }, [])}

            {/* space before D🌍T */}
            <span className="inline-block w-[0.26em]" />
            {/* D🌍T */}
            <span className="inline-flex items-center">
              {["D", "T"].map((ch, ci) => {
                const idx = letterIdx++;
                return (
                  <span key={`dot-${ci}`} className="inline-flex items-center">
                    {ci === 0 && (
                      <motion.span
                        custom={idx}
                        variants={letterVariant}
                        initial={false}
                        animate="show"
                        className="inline-block"
                      >
                        D
                      </motion.span>
                    )}
                    {ci === 0 && (
                      <span className="inline-flex h-[0.92em] w-[0.92em] mx-[0.04em] -translate-y-[0.02em] items-center justify-center">
                        <motion.span
                          variants={globeAnim}
                          initial={false}
                          animate={["show"]}
                          className="inline-flex h-full w-full"
                        >
                          {/* 3D stylized globe as the "O", Pakistan green, axis tilted east */}
                          <StylizedGlobe className="h-full w-full" spinDegPerSec={75} />
                        </motion.span>
                      </span>
                    )}
                    {ci === 1 && (
                      <motion.span
                        custom={idx}
                        variants={letterVariant}
                        initial={false}
                        animate="show"
                        className="inline-block"
                      >
                        T
                      </motion.span>
                    )}
                  </span>
                );
              })}
            </span>
          </motion.div>

          {/* underline sweep, width matches the wordmark text only */}
          <motion.div
            className="h-[2px] w-fit rounded-full bg-gradient-to-r from-[#2563eb] via-[#56c267] to-transparent"
            style={{ minWidth: "min(520px, 80vw)" }}
            initial={{ scaleX: 0, originX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 }}
          />
        </motion.div>

        {/* ── Green Tourism Pakistan Initiative — hidden per request; Pakistan Tourism
            Almanac below now takes this line's exact position/size/style/color ── */}

        {/* ── Pakistan Tourism Almanac ── */}
        <motion.span
          custom={0.85}
          variants={fadeUp}
          initial={false}
          animate="show"
          className="mt-2 font-[family-name:var(--font-din)] font-bold text-[clamp(1rem,1.8vw,1.35rem)] text-[rgba(255,247,231,0.92)] tracking-[0.01em]"
          style={{ textShadow: "0 2px 12px rgba(0,0,0,0.85)" }}
        >
          Pakistan Tourism Almanac
        </motion.span>
      </motion.div>

      {/* ── Green Tourism Pakistan philosophy quote, typewriter ── */}
      <motion.figure
        ref={quoteRef}
        aria-hidden={quoteDone || undefined}
        initial={false}
        animate={{ opacity: quoteDone ? 0 : 1, y: quoteDone && !reduceMotion ? 8 : 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.6, ease: "easeOut" }}
        className="mt-3 max-w-[560px] rounded-xl border border-[rgba(255,255,255,0.16)] bg-[rgba(7,17,13,0.45)] px-4 py-3 backdrop-blur-md sm:mt-5 sm:px-6 sm:py-5"
      >
        {/* Smaller and tighter on mobile - at the desktop size this ran to five
            or six lines on a phone and dominated the hero. */}
        <blockquote className="m-0 font-[family-name:var(--font-display)] font-light italic text-[0.82rem] leading-[1.55] text-[rgba(255,247,231,0.92)] sm:text-[clamp(0.95rem,1.4vw,1.2rem)] sm:leading-[1.7]">
          &ldquo;{QUOTE}&rdquo;
        </blockquote>
        <figcaption
          className="mt-1.5 font-[family-name:var(--font-display)] italic text-[0.7rem] text-[rgba(255,247,231,0.5)] sm:mt-2 sm:text-[clamp(0.78rem,1vw,0.9rem)]"
        >
          ~ Green Tourism Pakistan
        </figcaption>
      </motion.figure>
    </div>
  );
}
